import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { DragDropModule, CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { PageBreadcrumbComponent } from '../../../shared/components/common/page-breadcrumb/page-breadcrumb.component';
import { DealContactRoleApiService } from '../services/deal-contact-role-api.service';
import { DealContactRoleResponse } from '../models/deal-contact-role.model';
import { AuthService } from '../../auth-pages/services/auth.service';
import { ConfirmDialogService } from '../../../shared/services/confirm-dialog.service';
import { ToastService } from '../../../shared/services/toast.service';
import { HasPermissionDirective } from '../../../shared/directives/has-permission.directive';
import { PermissionService } from '../../../shared/services/permission.service';

@Component({
  selector: 'app-deal-contact-role-list',
  standalone: true,
  imports: [CommonModule, RouterModule, FormsModule, DragDropModule, PageBreadcrumbComponent, HasPermissionDirective],
  templateUrl: './deal-contact-role-list.component.html',
  styleUrls: ['./deal-contact-role-list.component.css'],
})
export class DealContactRoleListComponent implements OnInit {
  items: DealContactRoleResponse[] = [];
  loading = false;
  error = '';
  /** Reorden en vuelo: evita drags encadenados mientras el POST no confirma. */
  reordering = false;

  constructor(
    private api: DealContactRoleApiService,
    private authService: AuthService,
    private router: Router,
    private confirmDialog: ConfirmDialogService,
    private toast: ToastService,
    private permissions: PermissionService,
  ) {}

  /** POST /api/deal-contact-roles/reorder exige settings:EDIT (no deals:edit como los botones). */
  get canReorder(): boolean {
    return this.permissions.has('settings', 'edit');
  }

  ngOnInit(): void {
    this.cargar();
  }

  cargar(): void {
    const businessId = this.authService.getCurrentUser()?.businessId;
    if (!businessId) {
      this.error = 'No se pudo obtener el negocio del usuario';
      return;
    }

    this.loading = true;
    this.error = '';

    this.api.getAll(businessId).subscribe({
      next: (data) => {
        this.items = data;
        this.loading = false;
      },
      error: (err) => {
        this.error =
          err.error?.message || 'Error al cargar los roles de contacto';
        this.loading = false;
        console.error('Error:', err);
      },
    });
  }

  /**
   * Reordena por drag & drop. `getAll` devuelve la lista completa del negocio sin paginar,
   * así que `items` se puede enviar entero al endpoint de reorden.
   *
   * Estrategia optimista: se mueve la fila y se renumera la columna "Orden" (1..N) al instante;
   * si la API falla se revierte el orden y el displayOrder exactos que había antes.
   * Sin toast en el éxito (regla de drag-drop del skill crm-toast-notifications).
   */
  dropRol(event: CdkDragDrop<DealContactRoleResponse[]>): void {
    if (event.previousIndex === event.currentIndex) return;
    if (!this.canReorder || this.reordering) return;

    const businessId = this.authService.getCurrentUser()?.businessId;
    if (!businessId) return;

    // Snapshot para revertir: conserva posición Y displayOrder previos de cada ítem.
    const snapshot = this.items.map(i => ({ item: i, displayOrder: i.displayOrder }));

    moveItemInArray(this.items, event.previousIndex, event.currentIndex);
    this.items.forEach((i, idx) => i.displayOrder = idx + 1);

    const orderedIds = this.items.map(i => i.id);

    this.reordering = true;
    this.api.reorder(businessId, orderedIds).subscribe({
      next: () => {
        this.reordering = false;
      },
      error: (err) => {
        this.reordering = false;
        // Revertir: la UI no puede quedar mostrando un orden que el backend rechazó.
        this.items = snapshot.map(s => {
          s.item.displayOrder = s.displayOrder;
          return s.item;
        });
        this.toast.error({
          title: 'Error',
          message: err.error?.message || 'Error al reordenar los roles de contacto'
        });
      }
    });
  }

  nuevo(): void {
    this.router.navigate(['/config-deals/roles-contacto/crear']);
  }

  editar(item: DealContactRoleResponse): void {
    this.router.navigate(['/config-deals/roles-contacto/editar', item.id]);
  }

  toggleActivo(item: DealContactRoleResponse): void {
    const businessId = this.authService.getCurrentUser()?.businessId;
    if (!businessId) return;

    const accion = item.isActive
      ? this.api.deactivate(item.id, businessId)
      : this.api.activate(item.id, businessId);
    accion.subscribe({
      next: () => {
        this.cargar();
      },
      error: (err) => {
        this.confirmDialog.alert({
          title: 'Error',
          message: err.error?.message || 'Error al cambiar el estado del rol',
          variant: 'danger',
        });
      },
    });
  }

  async eliminar(item: DealContactRoleResponse): Promise<void> {
    const businessId = this.authService.getCurrentUser()?.businessId;
    if (!businessId) return;

    const ok = await this.confirmDialog.confirmDanger({
      title: 'Eliminar rol',
      message: `¿Está seguro de eliminar el rol "${item.name}"?`,
    });
    if (!ok) return;
    this.api.delete(item.id, businessId).subscribe({
      next: () => {
        this.cargar();
        this.toast.deleted('Rol de contacto', `Se eliminó "${item.name}" correctamente.`);
      },
      error: (err) => {
        this.confirmDialog.alert({
          title: 'Error',
          message: err.error?.message || 'Error al eliminar el rol',
          variant: 'danger',
        });
        this.toast.error({
          title: 'Error',
          message: err.error?.message || 'Error al eliminar el rol',
        });
      },
    });
  }
}
